<template>
  <div class="form">
    <div class="form__group">
      <select-input
        id="currency"
        name="currency"
        label="Currency"
        placeholder="Choose"
        :options="prices"
        v-model="currency"
      />
    </div>
    <div class="form__group">
      <text-input
        id="email"
        type="email"
        label="Email"
        v-model="email"
        placeholder="email@address.com"
        :disabled="hasUser"
      />
    </div>
    <div class="form__group" v-if="userHasPaid">
      <div class="form__paid">
        You already paid for {{season.name}}
      </div>
    </div>
    <div class="form__group" v-else>
      <discount v-bind="payment" v-model="discount" />
    </div>
    <div class="form__group" v-if="!hasUser">
      <span v-if="userHasPaid">
        <button @click="login">Login</button>
      </span>
      <span v-else-if="currency === 'GBP'">
        <paypal-button
          v-bind="payment"
          @complete="onPayment"
          :disabled="disablePayButton"
        />
      </span>
      <span v-else>
        <paystack-button
          v-bind="payment"
          @complete="onPayment"
          :disabled="disablePayButton"
        />
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { CURRENCY_NAMES, formatMoney } from '@/utilities/money';
import TextInput from '@/components/TextInput.vue';
import SelectInput from '@/components/SelectInput.vue';
import PaystackButton from '@/components/PaystackButton.vue';
import PaypalButton from '@/components/PaypalButton.vue';
import Discount from './Discount.vue';

export default {
  name: 'PaymentForm',
  props: {
    season: Object,
    value: Object,
  },
  components: {
    TextInput,
    SelectInput,
    PaystackButton,
    PaypalButton,
    Discount,
  },
  data() {
    return {
      email: null,
      currency: null,
      discount: null,
      userHasPaid: false,
    };
  },
  computed: {
    ...mapGetters(['getUser']),
    hasUser() {
      return Boolean(this.getUser);
    },
    prices() {
      const getLabel = ({ amount, currency }) => {
        const price = formatMoney({ amount, currency });
        return `${CURRENCY_NAMES[currency]} - ${price}`;
      };
      return this.season.prices.map(price => ({
        key: price.currency,
        label: getLabel(price),
      }));
    },
    price() {
      const { prices } = this.season;
      const getPrice = () => prices.find(price => price.currency === this.currency);
      return this.currency && getPrice();
    },
    payment() {
      if (this.currency) {
        const discount = (this.discount && this.discount.amount) || 0;
        const amount = this.price.amount - discount;
        return {
          description: `JAND ${this.season.name}`,
          email: this.getUser ? this.getUser.email : this.email,
          currency: this.currency,
          amount,
        };
      }
      return null;
    },
    disablePayButton() {
      return this.userHasPaid || !(this.email && this.payment);
    },
  },
  created() {
    this.setCurrencyFromIP();
    if (this.getUser) {
      this.email = this.getUser.email;
    }
  },
  watch: {
    currency() {
      this.$emit('input', this.payment);
    },
    email(value) {
      this.userHasPaid = false;
      if (value) {
        this.checkIfUserHasPaid();
      }
    },
  },
  methods: {
    ...mapActions(['fetchPayments', 'savePayment']),
    setCurrencyFromIP() {
      const vm = this;
      fetch('http://ip-api.com/json')
        .then(response => response.json())
        .then((response) => {
          const { countryCode } = response;
          vm.currency = countryCode === 'UK' ? 'GBP' : 'NGN';
        }).finally(() => {
          vm.currency = vm.currency || 'NGN';
        });
    },
    checkIfUserHasPaid() {
      this.fetchPayments(this.email).then((response) => {
        this.userHasPaid = Boolean(response && response.length);
      });
    },
    onPayment({ processor, response }) {
      const payment = {
        ...this.payment,
        processor,
        response,
        season: this.season,
        discount: this.discount,
      };
      this.savePayment(payment).then(() => this.$emit('login', {
        email: this.email,
        justPaid: true,
      }));
    },
    login() {
      this.$emit('login', { email: this.email });
    },
  },
};
</script>

<style lang="scss" scoped>
.form {
  justify-self: center;
  max-width: 400px;
  margin: 0 0 30px;
  &:last-child {
    margin-bottom: 0;
  }
  h3 {
    margin-top: 0;
    margin-bottom: 5px;
  }
  p {
    line-height: 1.3;
    margin-top: 0;
    margin-bottom: 30px;
  }
  &__group {
    margin-top: 15px;
    position: relative;
  }
  &__paid {
    text-align: center;
    font-size: var(--font-small);
    color: var(--color-link-inactive);
  }
  button {
    width: 100%;
  }
}
</style>
